import React, { useCallback, useMemo } from 'react';
import { parseISO } from 'date-fns';

import { TFunction } from '@lyearn/i18n';
import { RendererParams, SetFieldValue, useFormFieldValue } from '@/components/Form';
import { EventStatusEnum, EventUnion } from '@/types/schema';

import RecurringDateRange from './RecurringDateRange';

type FromDateRangePicker = {
  setFieldValue: SetFieldValue;
  startTime: string;
  endTime: string;
  showError: boolean;
  errorDate: any;
  warnings?: string[];
  disabled?: boolean;
  direction?: 'row' | 'col';
};

const FormDateRangePicker = ({
  setFieldValue,
  startTime,
  endTime,
  showError,
  errorDate,
  disabled,
  direction,
}: FromDateRangePicker) => {
  const onChange = useCallback(
    (newStartTime: string, newEndTime: string) => {
      if (startTime !== newStartTime) {
        setFieldValue('startTime', newStartTime, true);
      }

      if (endTime !== newEndTime) {
        setFieldValue('endTime', newEndTime, true);
      }
    },
    [endTime, setFieldValue, startTime],
  );

  const datePickerValues = useMemo(
    () => ({ startTime: parseISO(startTime), endTime: parseISO(endTime) }),
    [endTime, startTime],
  );

  return (
    <RecurringDateRange
      direction={direction}
      disabled={disabled}
      error={showError ? errorDate : undefined}
      values={datePickerValues}
      onChange={onChange}
    />
  );
};

export default React.memo(FormDateRangePicker);

export const renderSessionDateRangePicker =
  ({
    t,
    status,
    direction,
    initialEvent,
  }: {
    t: TFunction;
    status?: EventStatusEnum;
    direction?: 'row' | 'col';
    initialEvent?: EventUnion;
  }) =>
  ({ errors, formik, setFieldValue }: RendererParams) => {
    const startTime = useFormFieldValue('startTime');
    const endTime = useFormFieldValue('endTime');
    const showError = Boolean(
      (formik.touched?.startTime || formik.touched?.endTime) && errors?.date,
    );

    return (
      <FormDateRangePicker
        direction={direction}
        disabled={status === EventStatusEnum.Completed}
        endTime={endTime}
        errorDate={errors?.date}
        setFieldValue={setFieldValue}
        showError={showError}
        startTime={startTime}
      />
    );
  };
